import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import {
    House,
    Briefcase,
    MagnifyingGlass,
    FileText,
    Scroll,
    Books,
    Buildings,
    Crown,
    Gear,
    SignOut,
    Scales as ScalesSimple,
    Users,
    CheckSquare,
    Calendar as CalendarIcon,
    ListChecks,
    Gavel,
    Receipt,
    BookBookmark,
    ChartLineUp,
    ShieldCheck,
} from "@phosphor-icons/react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import BudgetBanner from "@/components/BudgetBanner";
import GlobalSearch from "@/components/GlobalSearch";
import HelpButton from "@/components/HelpButton";
import { superadminApi } from "@/lib/api";
import { toast } from "sonner";

function ImpersonationBanner({ user }) {
    const exit = async () => {
        try {
            await superadminApi.stopImpersonate();
            window.location.href = "/admin";
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not exit support mode");
        }
    };
    return (
        <div className="bg-amber-500 text-amber-950 px-4 py-2 flex items-center justify-between gap-3 text-sm" data-testid="impersonation-banner">
            <div className="flex items-center gap-2 min-w-0">
                <ShieldCheck size={16} weight="fill" className="shrink-0" />
                <span className="truncate">
                    <strong>Support Mode</strong> — acting as {user?.email}. All actions are audit-logged.
                </span>
            </div>
            <button
                onClick={exit}
                className="shrink-0 border border-amber-900/40 rounded-sm px-3 py-1 text-xs font-medium hover:bg-amber-400 transition-colors"
                data-testid="impersonation-exit-btn"
            >
                Exit Support Mode
            </button>
        </div>
    );
}

const NAV = [
    { to: "/dashboard", label: "Dashboard", icon: House, testid: "nav-dashboard" },
    { to: "/clients", label: "Clients", icon: Users, testid: "nav-clients" },
    { to: "/matters", label: "Matters", icon: Briefcase, testid: "nav-matters" },
    { to: "/calendar", label: "Calendar", icon: CalendarIcon, testid: "nav-calendar" },
    { to: "/cause-lists", label: "Cause Lists", icon: ListChecks, testid: "nav-cause-lists" },
    { to: "/court-search", label: "Court Search", icon: Gavel, testid: "nav-court-search" },
    { to: "/tasks", label: "Tasks", icon: CheckSquare, testid: "nav-tasks" },
    { to: "/billing", label: "Billing", icon: Receipt, testid: "nav-billing" },
    { to: "/analytics", label: "Analytics", icon: ChartLineUp, testid: "nav-analytics" },
    { to: "/research", label: "Research", icon: MagnifyingGlass, testid: "nav-research" },
    { to: "/drafting", label: "Drafting", icon: Scroll, testid: "nav-drafting" },
    { to: "/clauses", label: "Clause Bank", icon: BookBookmark, testid: "nav-clauses" },
    { to: "/precedents", label: "Precedents", icon: Books, testid: "nav-precedents" },
    { to: "/playbooks", label: "Playbooks", icon: ListChecks, testid: "nav-playbooks" },
    { to: "/compliance", label: "Compliance", icon: ShieldCheck, testid: "nav-compliance" },
    { to: "/documents", label: "Documents", icon: FileText, testid: "nav-documents" },
    { to: "/vault", label: "Knowledge Vault", icon: Books, testid: "nav-vault" },
    { to: "/chambers", label: "Chambers", icon: Buildings, testid: "nav-chambers" },
    { to: "/settings", label: "Settings", icon: Gear, testid: "nav-settings" },
];

export default function AppShell({ children }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const initials = (user?.name || user?.email || "A").slice(0, 1).toUpperCase();
    const [searchOpen, setSearchOpen] = useState(false);
    useEffect(() => {
        const handler = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setSearchOpen((s) => !s);
            }
            if (e.key === "/" && document.activeElement?.tagName !== "INPUT" && document.activeElement?.tagName !== "TEXTAREA") {
                e.preventDefault();
                setSearchOpen(true);
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, []);

    return (
        <div className="min-h-screen parchment grain relative" data-testid="app-shell">
            <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
            <div className="flex min-h-screen">
                {/* Sidebar */}
                <aside
                    className="hidden md:flex md:w-64 flex-col border-r border-border bg-white/70 backdrop-blur-sm sticky top-0 h-screen"
                    data-testid="sidebar"
                >
                    <Link to="/dashboard" className="px-5 py-5 flex items-center gap-2 border-b border-border">
                        <ScalesSimple size={22} weight="duotone" className="text-primary" />
                        <div>
                            <div className="font-serif text-lg leading-none tracking-tight">CounselIQ</div>
                            <div className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mt-1">
                                Advocate Workspace
                            </div>
                        </div>
                    </Link>

                    <nav className="flex-1 overflow-y-auto px-2 py-4 space-y-0.5 scrollbar-slim">
                        {NAV.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                data-testid={item.testid}
                                className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                            >
                                <item.icon size={18} weight="duotone" />
                                <span>{item.label}</span>
                            </NavLink>
                        ))}
                        {["admin", "superadmin"].includes(user?.role) && (
                            <NavLink
                                to="/admin"
                                data-testid="nav-admin"
                                className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                            >
                                <Crown size={18} weight="duotone" />
                                <span>Admin</span>
                            </NavLink>
                        )}
                    </nav>

                    <button
                        onClick={logout}
                        data-testid="logout-btn"
                        className="m-3 flex items-center gap-3 px-3 py-2 rounded-sm text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors border border-border"
                    >
                        <SignOut size={16} />
                        <span>Sign out</span>
                    </button>
                </aside>

                {/* Main */}
                <main className="flex-1 min-w-0 flex flex-col">
                    {user?.impersonation && <ImpersonationBanner user={user} />}
                    <BudgetBanner />
                    <header className="sticky top-0 z-20 bg-background/85 backdrop-blur-md border-b border-border">
                        <div className="flex items-center justify-between px-4 md:px-8 py-3">
                            <div className="flex items-center gap-3 min-w-0">
                                {/* mobile nav button */}
                                <select
                                    data-testid="mobile-nav"
                                    className="md:hidden text-sm border border-border rounded-sm px-2 py-1 bg-white"
                                    value={location.pathname.split("/")[1] || "dashboard"}
                                    onChange={(e) => navigate("/" + e.target.value)}
                                >
                                    {NAV.map((n) => (
                                        <option key={n.to} value={n.to.replace("/", "")}>
                                            {n.label}
                                        </option>
                                    ))}
                                </select>
                                <div className="hidden md:block text-xs uppercase tracking-[0.25em] text-muted-foreground">
                                    Professional · Bilingual · Citation-first
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={() => setSearchOpen(true)}
                                    className="hidden sm:inline-flex items-center gap-2 text-xs text-muted-foreground border border-border rounded-sm px-2.5 py-1 hover:bg-muted/40"
                                    data-testid="global-search-trigger"
                                >
                                    <MagnifyingGlass size={13} />
                                    <span>Search</span>
                                    <kbd className="ml-2 text-[10px] border border-border rounded-sm px-1 py-0.5">⌘K</kbd>
                                </button>
                                <HelpButton variant="text" label="Help" testid="header-help-btn" />
                                <div className="text-sm text-muted-foreground hidden sm:block">
                                    {user?.email}
                                </div>
                                <Avatar data-testid="user-avatar" className="h-8 w-8 ring-1 ring-border">
                                    <AvatarImage src={user?.picture} alt={user?.name} />
                                    <AvatarFallback>{initials}</AvatarFallback>
                                </Avatar>
                            </div>
                        </div>
                    </header>
                    <div className="flex-1 min-w-0 pb-20 md:pb-16">{children}</div>
                </main>
            </div>
        </div>
    );
}
